/**
 * 建立柜档案弹窗（移动端版，对齐后台 ContainerArchive.tsx 的「建立柜档案」Dialog）。
 *
 * 触发场景：
 * - NodeSubstepPanel 的 needArchiveCreate 动作入口（onRequestNewArchive 回调）——建柜主路径
 * - （历史）advance 返 NEED_CONTAINER 软阻断——已废弃，建柜改由上述入口驱动
 *
 * 行为（与 admin 一致）：
 * - 一单多柜：可添加/删除多行，柜号 + 柜型必填；封号/船名/航次/ETA 选填
 * - 点「创建并继续」：逐柜走 form_data（saveFormData，nodeCode=load, substepCode=archive_create）
 *   → 后端投影器 upsert containers；提交成功后回调 onCreated(rows) 由父级 retry 推进
 * - 「取消」= 软退出：不建档、不推进，节点状态不变，之后随时还能再调出本弹窗
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { listFormTemplates, saveFormData } from '@/api/form';
import type { FormTemplate, ContainerArchiveFormItem, ContainerTypeValue } from '@/api/types';
import { useDict } from '@/hooks/useDict';

function emptyRow(): ContainerArchiveFormItem {
  return { containerNo: '', containerType: 'REEFER_20', sealNo: '', vesselName: '', voyageNo: '', eta: '' };
}

interface CreateContainerDialogProps {
  open: boolean;
  orderId: string;
  /** 预填柜号（来自 node-action-hint.prefillContainerNo） */
  prefillContainerNo?: string;
  /** 关闭弹窗（软退出，不建档不推进） */
  onClose: () => void;
  /** 所有柜创建成功后回调，父级据此 retry 推进 */
  onCreated: (rows: ContainerArchiveFormItem[]) => void;
}

export default function CreateContainerDialog({
  open,
  orderId,
  prefillContainerNo,
  onClose,
  onCreated,
}: CreateContainerDialogProps) {
  const [rows, setRows] = useState<ContainerArchiveFormItem[]>([emptyRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [tpl, setTpl] = useState<FormTemplate | null>(null);
  const [snack, setSnack] = useState<{ msg: string; sev: 'error' | 'info' } | null>(null);

  const { dictData } = useDict();
  const containerTypeOptions = Object.entries(dictData['container_type'] || {}).map(
    ([value, label]) => ({ value: value as ContainerTypeValue, label }),
  );

  // 弹窗打开时：查找 container_archive 模板 + 应用预填柜号 + 重置表单
  useEffect(() => {
    if (!open) return;
    setRows([{ ...emptyRow(), containerNo: prefillContainerNo || '' }]);
    setSubmitting(false);
    setSnack(null);
    listFormTemplates()
      .then((ts) => setTpl((ts || []).find((t) => t.templateCode === 'container_archive') || null))
      .catch((err) => {
        console.error('加载柜档案模板失败', err);
        setTpl(null);
      });
  }, [open, prefillContainerNo]);

  const handleChange = useCallback(
    (index: number, patch: Partial<ContainerArchiveFormItem>) => {
      setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    },
    [],
  );

  const handleAdd = useCallback(() => setRows((prev) => [...prev, emptyRow()]), []);

  const handleRemove = useCallback(
    (index: number) => setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index))),
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (rows.length === 0) {
      setSnack({ msg: '请至少添加一个柜', sev: 'error' });
      return;
    }
    if (rows.some((r) => !r.containerNo.trim())) {
      setSnack({ msg: '柜号不能为空', sev: 'error' });
      return;
    }
    if (rows.some((r) => !r.containerType)) {
      setSnack({ msg: '柜型不能为空', sev: 'error' });
      return;
    }
    if (!tpl) {
      setSnack({ msg: '未找到柜档案表单模板（container_archive）', sev: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      // 逐柜提交 form_data（与 admin submitContainerArchive 完全一致：POST /form/data）
      for (const row of rows) {
        const payload: ContainerArchiveFormItem = {
          containerNo: row.containerNo.trim(),
          containerType: row.containerType,
          sealNo: row.sealNo?.trim() || undefined,
          vesselName: row.vesselName?.trim() || undefined,
          voyageNo: row.voyageNo?.trim() || undefined,
          eta: row.eta?.trim() || undefined,
        };
        await saveFormData({
          orderId,
          nodeCode: 'load',
          substepCode: 'archive_create',
          templateId: tpl.id,
          data: JSON.stringify(payload),
          status: 'submitted',
        });
      }
      onCreated(rows);
    } catch (err: unknown) {
      setSnack({ msg: (err as { message?: string })?.message || '创建失败', sev: 'error' });
    } finally {
      setSubmitting(false);
    }
  }, [rows, tpl, orderId, onCreated]);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        component="div"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 1 }}
      >
        <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>建立柜档案</Typography>
        <IconButton onClick={onClose} disabled={submitting} size="small" sx={{ p: 0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 2, pt: 1 }}>
        <Typography sx={{ mb: 1.5, color: 'text.secondary', fontSize: '12px' }}>
          提交「装柜」节点需要先建立柜档案（支持一单多柜）。请逐柜填写：
        </Typography>

        {rows.map((row, index) => (
          <Box
            key={index}
            sx={{
              p: 1.5,
              mb: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '10px',
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>柜 {index + 1}</Typography>
              <IconButton
                size="small"
                sx={{ color: 'text.disabled' }}
                onClick={() => handleRemove(index)}
                disabled={submitting || rows.length <= 1}
                aria-label="删除该柜"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              <TextField
                size="small"
                label="柜号 *"
                placeholder="如 CSLU2345678"
                value={row.containerNo}
                onChange={(e) => handleChange(index, { containerNo: e.target.value })}
                disabled={submitting}
              />
              <TextField
                size="small"
                select
                label="柜型 *"
                value={row.containerType}
                onChange={(e) => handleChange(index, { containerType: e.target.value as ContainerTypeValue })}
                disabled={submitting}
              >
                {containerTypeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="封号"
                placeholder="如 SEAL001"
                value={row.sealNo || ''}
                onChange={(e) => handleChange(index, { sealNo: e.target.value })}
                disabled={submitting}
              />
              <TextField
                size="small"
                label="船名"
                placeholder="如 EVER GIVEN"
                value={row.vesselName || ''}
                onChange={(e) => handleChange(index, { vesselName: e.target.value })}
                disabled={submitting}
              />
              <TextField
                size="small"
                label="航次"
                placeholder="如 123E"
                value={row.voyageNo || ''}
                onChange={(e) => handleChange(index, { voyageNo: e.target.value })}
                disabled={submitting}
              />
              <TextField
                size="small"
                label="预计到港"
                type="date"
                value={row.eta || ''}
                onChange={(e) => handleChange(index, { eta: e.target.value })}
                InputLabelProps={{ shrink: true }}
                disabled={submitting}
              />
            </Box>
          </Box>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={submitting}
          sx={{ mb: 0.5, textTransform: 'none', fontSize: '13px' }}
          fullWidth
        >
          添加柜
        </Button>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, pt: 0, gap: 1.5 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={submitting}
          sx={{ flex: 1, height: 44, textTransform: 'none', borderRadius: '10px' }}
        >
          取消
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ flex: 1, height: 44, textTransform: 'none', borderRadius: '10px' }}
        >
          {submitting ? <CircularProgress size={20} /> : '创建并继续'}
        </Button>
      </DialogActions>

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setSnack(null)}
      >
        <Alert severity={snack?.sev} sx={{ fontSize: '13px' }}>
          {snack?.msg}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
